// Copy of the "Personnel planning" walkthrough. The rules every text follows are in ../../de/help.js.
export default {
  title: "Personnel planning",
  // Short names of the views, for the header, the timeline and "Continue to …".
  section: {
    PersonnelPlanning: "Calendar list",
    PersonnelCalendar: "Calendar",
    SettingsOverview: "Absence kinds",
    ProjectDetail: "Project phases",
    EmployeeOverview: "Staff",
    TimeTrackingMy: "My times",
    TimeTrackingReview: "Week grid",
    // Both time tracking pages together, as one topic on the timeline.
    TimeTracking: "Time tracking",
  },
  // The pages outside personnel planning a calendar takes its contents from, and the ones the plan
  // shows up on again.
  related: "Connections",
  step: {
    pinned: {
      text: "Pinned calendars sit at the top, all others below.",
    },
    "pin-drag": {
      text: "Drag a card up to the pinned ones with the mouse, and back down the same way. The pin on the card does the same with one click.",
    },
    "card-pin": {
      text: "The pin keeps a calendar at the top among the pinned ones. The layers icon beside it says the calendar holds several projects.",
    },
    card: {
      text: "This icon opens the calendar. The card already shows its name and, as a preview, when its projects run.",
    },
    "card-options": {
      text: "The three dots are where you rename the calendar.",
    },
    create: {
      text: "This is where you start a new calendar. You give it a name and pick its projects.",
    },

    intro: {
      text: "A calendar plans which of your people are on which site, and when. The page has three sections: Projects, Employees and Capacity.",
    },
    head: {
      text: "The Actions menu detaches the calendar into a window of its own, renames it, opens the project details or deletes it.",
    },
    dates: {
      text: "The arrows page back and forth by day, week or month. Today brings the current day back into view.",
    },
    zoom: {
      text: "The magnifiers zoom out and back in, so more or fewer days fit side by side. The middle one restores the original size.",
    },
    projects: {
      text: "The Projects section shows every project of the calendar as a bar over its build time. The plus opens up its phases.",
    },
    employees: {
      text: "Here are the calendar's crews and staff with their employee position. A bar says who is on which project and when.",
    },
    capacity: {
      text: 'Capacity puts the need next to the staff. Planned is the sum of all phases, Staff the number of people booked that day. Click a phase in the legend to drag its line. With only one project on the calendar, drag the Planned line itself. For a whole period, use "Edit capacity" in the section bar. The numbers below the chart show staff minus planned: red means people are missing.',
    },
    // The six gestures on the board. The pointer really makes them, so the texts say what is
    // happening rather than what could be done.
    mark: {
      text: "Hold the mouse button down and drag across the days of a row. The marked days are the period you are about to plan.",
    },
    "menu-days": {
      text: "The right mouse button on the marked days opens their menu. From here you assign a project or record an absence.",
    },
    assign: {
      text: "Drag the marked days onto the row of a project. The row shows beforehand who is planned on which days, and letting go makes it so, with no form in between.",
    },
    move: {
      text: "A bar is dragged left or right along its own row and snaps to whole days. Pulled by one of its ends it gets longer or shorter.",
    },
    "menu-span": {
      text: "Every bar has a menu of its own as well. It changes the dates, moves the work to another project, or deletes it again.",
    },
    // The board's menus are written in the product in German, so the English text describes the
    // entry rather than quoting a label that is not there.
    editor: {
      text: "The entry at the top opens the two date fields. The buttons below them move the end, and every click adds up. Save takes the new period over, Cancel leaves the bar exactly as it is.",
    },
    "menu-project-row": {
      text: "The name column opens a menu with the right mouse button too. On a project it shows the preview, changes the timeline and colour or adds a phase.",
    },
    "menu-person-row": {
      text: "On a person it opens the preview or the details page, assigns a team or project, and adds more staff or teams to the calendar.",
    },
    resize: {
      text: "Drag the line between two sections up or down. A double-click restores the original split.",
    },
    enlarge: {
      text: "This icon hands one section the whole page. The others stay as thin bars and can be opened up again.",
    },

    again: { text: "This button is on every page that has an explanation. One click starts it again." },

    "absence-pick": {
      text: "An absence is entered on marked days, through their menu. In the window you pick its kind, holiday for example.",
    },
    "absence-types": {
      text: "The workspace's absence kinds are set up here. You pick from them in the calendar, and the kind's colour colours the bar.",
    },
    "project-phases": {
      text: "Phases are created on the project. In the calendar they are the project's sub-rows, the ones you plan people onto.",
    },
    "project-capacity": {
      text: "A phase's average need says how many people it takes on a working day. Those numbers make up the Planned line. Overlapping phases add up their need.",
    },
    staff: {
      text: "You can plan whoever is listed here as staff. Crews, in contrast, are put together inside the calendar itself.",
    },
    "time-suggestion": {
      text: "Whoever is planned on a project today finds it already filled in when clocking in. The line below says the suggestion comes from the plan.",
    },
    "time-absence": {
      text: "A whole day of absence from the calendar explains an empty day in time tracking. The week grid shows it as absent, and it does not count as missing time.",
    },
  },
}
